'use client'

import Footer from '@/components/Footer'
import ProductInterestForm from '@/components/ProductInterestForm'
import ImageFrame from '@/components/memorylinks/ImageFrame'

const featureCards = [
  {
    title: 'Save from anywhere',
    desc: 'Share from any app, paste manually, or capture from the clipboard.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
      </svg>
    ),
  },
  {
    title: 'Auto-filled details',
    desc: 'Titles, descriptions, tags, and source details are extracted automatically.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    title: 'Instant search',
    desc: 'Find links fast with local search, filters, favorites, and collections.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
  },
  {
    title: 'Works offline',
    desc: 'Your saved links stay available even without internet.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12a7 7 0 0114 0m-2 4a5 5 0 00-10 0m-2.5 3.5L19.5 4.5M9 17h.01M15 17h.01" />
      </svg>
    ),
  },
  {
    title: 'Encrypted backups',
    desc: 'Export and restore your library with passphrase-protected backups.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 11c1.657 0 3-1.343 3-3S13.657 5 12 5 9 6.343 9 8s1.343 3 3 3zm0 0v8m-4-4h8" />
      </svg>
    ),
  },
  {
    title: 'No account required',
    desc: 'Open the app and start saving immediately.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a3 3 0 11-6 0 3 3 0 016 0zm5 12a8 8 0 10-16 0h16z" />
      </svg>
    ),
  },
]

const screenshots = [
  {
    title: 'Home',
    desc: 'Search, filter, favorite, and jump back into saved links instantly.',
    image: '/memory_links_home_early_access.png?v=2',
    variant: 'portrait' as const,
  },
  {
    title: 'Capture',
    desc: 'Share from another app or paste a link directly into MemoryLinks.',
    image: '/memory_links_capture_early_access.png?v=2',
    variant: 'portrait' as const,
  },
  {
    title: 'Control Center',
    desc: 'Manage backups, auto-enrich, theme settings, and your Pro unlock.',
    image: '/memory_links_control_center_early_access.png?v=2',
    variant: 'portrait' as const,
  },
]

const howItWorks = [
  {
    step: '1',
    title: 'Capture',
    desc: 'Share a link into MemoryLinks or paste it directly.',
  },
  {
    step: '2',
    title: 'Review',
    desc: 'We fill in the details for you. Edit only if you want to.',
  },
  {
    step: '3',
    title: 'Find later',
    desc: 'Search, filter, favorite, and organize everything locally.',
  },
]

const pricingTiers = [
  {
    name: 'Free',
    price: '50 links',
    features: ['Core features', 'Offline-first', 'Encrypted backups'],
    highlighted: false,
  },
  {
    name: 'Pro',
    price: 'Unlimited links',
    features: ['Everything in Free', 'One-time unlock'],
    highlighted: true,
  },
]

const faqItems = [
  {
    q: 'Is my data private?',
    a: 'Yes. MemoryLinks stores your data locally on your device. No account is required.',
  },
  {
    q: 'Does it work offline?',
    a: 'Yes. The core experience is designed for offline use.',
  },
  {
    q: 'How do backups work?',
    a: 'You can export encrypted backups from Control Center and restore them later with your passphrase.',
  },
  {
    q: 'What platforms are supported?',
    a: 'Android is available now. iOS can come later.',
  },
]

export default function MemoryLinksPage() {
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white overflow-x-hidden">
      <section className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 px-6 sm:px-8 overflow-visible">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[780px] w-[780px] rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30">
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-blue-300">Android Early Access</span>
              </div>

              <div className="text-5xl sm:text-6xl md:text-7xl font-light tracking-tight">
                <span className="text-blue-400 relative">
                  <span className="absolute inset-0 blur-xl opacity-40 text-blue-500">MemoryLinks</span>
                  <span className="relative">MemoryLinks</span>
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-light leading-[1.08] tracking-tight max-w-4xl">
                Save links like you'll actually find them again.
              </h1>

              <p className="text-xl sm:text-2xl text-gray-200 leading-relaxed max-w-3xl">
                Offline-first link saver for Android. No account. No cloud. No tracking.
              </p>

              <p className="text-base sm:text-lg text-gray-400 leading-relaxed max-w-3xl">
                Capture links from any app, auto-fill titles and tags, search instantly, and keep everything stored locally on your device.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <button
                  onClick={() => scrollToSection('early-access')}
                  className="px-8 py-4 bg-blue-500 border border-blue-400/40 hover:bg-blue-400 text-white rounded-[16px] font-medium transition-colors text-base"
                >
                  Join Android early access
                </button>
                <button
                  onClick={() => scrollToSection('screenshots')}
                  className="px-8 py-4 border border-white/20 hover:border-white/40 text-white rounded-[16px] font-medium transition-colors text-base bg-white/5"
                >
                  See screenshots
                </button>
              </div>
            </div>

            <div className="lg:justify-self-end w-full max-w-[520px] mx-auto lg:mx-0">
              <ImageFrame
                src="/memory_links_home_early_access.png?v=2"
                alt="MemoryLinks Android home screen showing saved links, filters, favorites, and search"
                variant="portrait"
                priority
                className="scale-[1.03] md:scale-[1.08]"
              />
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="py-20 sm:py-28 px-6 sm:px-8 bg-[#0D1117]/35">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-4">Built for saving without losing track</h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Everything is optimized for quick capture, clean metadata, and local retrieval.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureCards.map((feature) => (
              <div
                key={feature.title}
                className="p-6 rounded-[20px] border border-white/10 bg-white/5 hover:border-blue-500/30 hover:bg-white/10 transition-all duration-200"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 p-2.5 rounded-[12px] bg-blue-500/20 text-blue-300">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="screenshots" className="py-20 sm:py-28 px-6 sm:px-8">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-4">Screenshots from the shipped app</h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              The current Android build already covers capture, local search, favorites, and encrypted backups.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
            {screenshots.map((shot) => (
              <div key={shot.title} className="space-y-5">
                <ImageFrame
                  src={shot.image}
                  alt={shot.title}
                  variant={shot.variant}
                />
                <div className="space-y-2 text-center">
                  <h3 className="text-2xl font-light">{shot.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed max-w-sm mx-auto">{shot.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-20 sm:py-28 px-6 sm:px-8 bg-[#0D1117]/35">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-4">How it works</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((item) => (
              <div key={item.step} className="p-8 rounded-[24px] border border-white/10 bg-white/5">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-500/10 border border-blue-500/30 mb-6">
                  <span className="text-xl font-light text-blue-300">{item.step}</span>
                </div>
                <h3 className="text-2xl font-light mb-3">{item.title}</h3>
                <p className="text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 px-6 sm:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-light">No account. No cloud. No tracking.</h2>
          <p className="text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto">
            MemoryLinks is local-first. Your saved links stay on your device, and encrypted backups are optional and under your control.
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <span className="px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-sm font-mono">
              Local-first
            </span>
            <span className="px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-sm font-mono">
              Offline-first
            </span>
            <span className="px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-sm font-mono">
              Encrypted backups
            </span>
            <span className="px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-sm font-mono">
              No tracking
            </span>
          </div>
        </div>
      </section>

      <section id="pricing" className="py-20 sm:py-28 px-6 sm:px-8 bg-[#0D1117]/35">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-4">Pricing</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {pricingTiers.map((tier) => (
              <div
                key={tier.name}
                className={`p-8 rounded-[24px] relative ${
                  tier.highlighted
                    ? 'border-2 border-blue-500/50 bg-blue-500/10'
                    : 'border border-white/10 bg-white/5'
                }`}
              >
                {tier.highlighted && (
                  <div className="absolute top-4 right-4 px-3 py-1 bg-blue-500 text-white text-xs font-mono rounded-full">
                    Early access
                  </div>
                )}
                <h3 className="text-2xl font-light mb-2">{tier.name}</h3>
                <div className="text-4xl font-light mb-6">{tier.price}</div>
                <ul className="space-y-3 text-gray-300">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="py-20 sm:py-28 px-6 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light mb-4">FAQ</h2>
          </div>

          <div className="space-y-4">
            {faqItems.map((faq) => (
              <details
                key={faq.q}
                className="p-6 rounded-[20px] border border-white/10 bg-white/5 group hover:border-blue-500/30 transition-all duration-200"
              >
                <summary className="text-lg font-medium cursor-pointer list-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 rounded-[12px] p-2 -m-2">
                  <span className="flex items-center justify-between">
                    {faq.q}
                    <span className="text-blue-400 ml-4 group-open:hidden">+</span>
                    <span className="text-blue-400 ml-4 hidden group-open:inline">−</span>
                  </span>
                </summary>
                <p className="text-gray-400 mt-4 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="early-access" className="py-20 sm:py-28 px-6 sm:px-8 bg-[#0D1117]/35">
        <div className="max-w-[1100px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-10 items-start">
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30">
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-blue-300">Android Early Access</span>
              </div>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-light leading-[1.08]">
                Join Android early access
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed">
                MemoryLinks is already working on Android. Join the list to get launch access, updates, and the first invite wave.
              </p>
              <p className="text-sm text-gray-500">
                Android is available now. iOS can come later.
              </p>
            </div>

            <div className="p-6 sm:p-8 rounded-[24px] border border-white/10 bg-white/5">
              <ProductInterestForm
                product="memorylinks"
                accentClass="blue"
                placeholderMessage="What kind of links do you want to save in MemoryLinks? (optional)"
                submitLabel="Join Android early access"
                successTitle="You're on the early access list."
                successBody="We'll send Android launch and access details when your invite is ready."
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
